/**
 * Model-space viewport tiling (Faz 4.9 — OCS pane_grid parity).
 *
 * OCS truth: there is no MDI cascade — CASCADE/HORIZONTAL/VERTICAL delegate
 * to VPORTS configurations (SIngle/2H/2V/4) over the model-space pane grid.
 * yap mirrors that with one config + per-cell pose store for the 2D shell:
 *
 *  - The ACTIVE cell hosts the interactive viewport (tool scope, osnap,
 *    command target); it keeps using the shared plan camera, so the command
 *    line always drives the cell the user last clicked.
 *  - The other cells render as live read-only preview canvases, each with
 *    its own zoom + pan target stored here (same pattern as the 3D
 *    workbench's useQuadViewStore), so poses survive cell switches.
 */

import { create } from 'zustand';

export type ModelVportsConfig = 'single' | '2h' | '2v' | '4';

/** Number of cell slots a config occupies (VPORTS Single/2H/2V/4). */
export const VPORTS_CELL_COUNT: Record<ModelVportsConfig, number> = {
    single: 1,
    '2h': 2,
    '2v': 2,
    '4': 4,
};

/** Base grid shape of a config (columns × rows) — join adjacency math. */
export const VPORTS_GRID: Record<ModelVportsConfig, { cols: number; rows: number }> = {
    single: { cols: 1, rows: 1 },
    '2h': { cols: 1, rows: 2 },
    '2v': { cols: 2, rows: 1 },
    '4': { cols: 2, rows: 2 },
};

/**
 * A saved VPORTS configuration (Faz 10.5 — named VPORTS): the base preset
 * plus the join overlay (groups of merged base-grid slots). A config with
 * no joins stores one span per slot, e.g. `[[0],[1],[2],[3]]` for the 4-grid.
 */
export interface NamedVportsConfig {
    config: ModelVportsConfig;
    spans: number[][];
}

/** Identity spans: every slot its own viewport (no joins). */
export function identitySpans(config: ModelVportsConfig): number[][] {
    return Array.from({ length: VPORTS_CELL_COUNT[config] }, (_, i) => [i]);
}

export interface ModelCellPose {
    /** Preview ortho zoom in pixels per world unit. */
    zoom: number;
    /** Pan target in world space (plan camera looks straight down Z). */
    target: [number, number];
}

interface ModelViewportsState {
    config: ModelVportsConfig;
    /** Cell that hosts the interactive viewport + receives command input. */
    activeCell: number;
    /** Per-cell poses for the PREVIEW cells (index-aligned with the slots). */
    cells: ModelCellPose[];
    /**
     * Join overlay over the current config (Faz 10.5): groups of merged
     * base-grid slots. Empty array = identity (no joins). Join = two
     * adjacent slots of one row/column become one viewport (AutoCAD VPORTS
     * Join; OCS has no Join verb — the AutoCAD behavior is written here).
     */
    spans: number[][];
    /** Named VPORTS configurations (Save/Restore/Delete). */
    namedConfigs: Record<string, NamedVportsConfig>;

    setConfig: (config: ModelVportsConfig) => void;
    setActiveCell: (cell: number) => void;
    setCellPose: (cell: number, patch: Partial<ModelCellPose>) => void;
    /** Join two adjacent cells into one viewport; returns an error string or null. */
    joinCells: (a: number, b: number) => string | null;
    saveNamed: (name: string) => void;
    restoreNamed: (name: string) => boolean;
    deleteNamed: (name: string) => boolean;
}

const INITIAL_POSE: ModelCellPose = { zoom: 20, target: [0, 0] };

export const useModelViewportsStore = create<ModelViewportsState>((set, get) => ({
    config: 'single',
    activeCell: 0,
    cells: [INITIAL_POSE, { ...INITIAL_POSE }, { ...INITIAL_POSE }, { ...INITIAL_POSE }],
    spans: [],
    namedConfigs: {},

    setConfig: (config) => {
        const count = VPORTS_CELL_COUNT[config];
        const cells = Array.from({ length: 4 }, (_, i) =>
            get().cells[i] ?? { ...INITIAL_POSE }
        );
        // Leaving the tiling always restores the single interactive view;
        // shrinking keeps the active cell inside the new slot count. A new
        // preset clears the join overlay (spans describe ONE tiling only).
        const activeCell = Math.min(get().activeCell, count - 1);
        set({ config, cells, activeCell, spans: [] });
    },
    setActiveCell: (activeCell) => {
        const { config } = get();
        if (activeCell >= 0 && activeCell < VPORTS_CELL_COUNT[config] && activeCell !== get().activeCell) {
            set({ activeCell });
        }
    },
    setCellPose: (cell, patch) => set((s) => ({
        cells: s.cells.map((pose, i) => (i === cell ? { ...pose, ...patch } : pose)),
    })),
    joinCells: (a, b) => {
        const { config, spans: current, activeCell } = get();
        const count = VPORTS_CELL_COUNT[config];
        if (config === 'single') return 'VPJOIN: nothing to join in the single viewport.';
        if (a === b) return 'VPJOIN: pick two different viewports.';
        if (a < 0 || b < 0 || a >= count || b >= count) {
            return `VPJOIN: viewport indexes are 1-${count} (pick with the dialog or type them).`;
        }
        const { cols } = VPORTS_GRID[config];
        const sameRow = Math.floor(a / cols) === Math.floor(b / cols);
        const sameCol = a % cols === b % cols;
        if (!sameRow && !sameCol) return 'VPJOIN: the two viewports are not adjacent.';
        const spans = current.length ? current.map((g) => [...g]) : identitySpans(config);
        const ga = spans.find((g) => g.includes(a));
        const gb = spans.find((g) => g.includes(b));
        if (!ga || !gb) return 'VPJOIN: invalid join state.';
        if (ga === gb) return 'VPJOIN: those viewports are already joined.';
        const merged = [...ga, b, ...gb.filter((x) => x !== a && x !== b)].sort((x, y) => x - y);
        const next = spans
            .filter((g) => g !== ga && g !== gb)
            .concat([merged])
            .sort((x, y) => x[0] - y[0]);
        // AutoCAD: the dominant viewport keeps its view; if the joined-away
        // cell was active, its slot now lives in the dominant's group.
        const active = merged.includes(activeCell) ? activeCell : get().activeCell;
        set({ spans: next, activeCell: active });
        return null;
    },
    saveNamed: (name) => {
        const { config, spans } = get();
        const trimmed = name.trim();
        if (!trimmed) return;
        set((s) => ({
            namedConfigs: {
                ...s.namedConfigs,
                [trimmed]: { config, spans: spans.length ? spans.map((g) => [...g]) : identitySpans(config) },
            },
        }));
    },
    restoreNamed: (name) => {
        const hit = get().namedConfigs[name];
        if (!hit) return false;
        const { config, spans } = hit;
        const count = VPORTS_CELL_COUNT[config];
        set({
            config,
            spans: spans.map((g) => [...g]),
            activeCell: Math.min(get().activeCell, count - 1),
        });
        return true;
    },
    deleteNamed: (name) => {
        if (!get().namedConfigs[name]) return false;
        set((s) => {
            const next = { ...s.namedConfigs };
            delete next[name];
            return { namedConfigs: next };
        });
        return true;
    },
}));
